import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { ArrowUpDownIcon, EllipsisVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react'
import {
	ConfirmDialog,
	Avatar, AvatarFallback, AvatarImage,
	Button,
	Checkbox,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
	IconButton,
	PanelFilters,
	SearchInput,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,

	Spinner,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
	Tabs, TabsList, TabsTrigger,
} from '@rentflowdev/ui'
import {
	useDeleteTaskMutation,
	useGetTaskQuery,
	useListTaskResponsiblesQuery,
	useListMeetingsQuery,
	useUpdateDealMeetingMutation,
	useListTasksQuery,
	useUpdateTaskMutation,
} from '@/api'
import { PanelDown } from '@/components/panel-down'
import { TaskDetailSheet } from '@/components/task-detail-sheet'
import { TaskFormDialog } from '@/components/task-form-dialog'
import { DEFAULT_LOCALE } from '@/types'
import type {
	Task,
	TaskBindingKind,
	TaskKind,
	TaskPriority,
	TaskSortKey,
	TaskStatus,
} from '@/types'
import { useCan } from '@/lib/access'

export const Route = createFileRoute('/_app/tasks')({
	validateSearch: z.object({
		taskId: z.string().optional(),
		due: z.enum(['today', 'week', 'month', 'overdue']).optional(),
	}),
	component: TasksPage,
})

const PAGE_SIZE = 10

const TASK_KINDS:    TaskKind[]        = ['CALL', 'MEETING', 'EMAIL', 'OTHER']

/**
 * A row of the list: a task, or a meeting wearing a task's clothes.
 *
 * `meetingId` is what tells them apart at the point of action — the two live
 * in different tables and answer to different endpoints.
 */
type TaskRow = Task & { meetingId?: string; dealId?: string }
const BINDING_KINDS: TaskBindingKind[] = ['DEAL', 'RENTAL', 'PROPERTY', 'CONTACT']

type DuePreset = 'all' | 'today' | 'week' | 'month' | 'overdue'
const DUE_PRESETS: DuePreset[] = ['today', 'week', 'month', 'overdue']

function startOfDay(d: Date): Date {
	const x = new Date(d)
	x.setHours(0, 0, 0, 0)
	return x
}

function endOfDay(d: Date): Date {
	const x = new Date(d)
	x.setHours(23, 59, 59, 999)
	return x
}

function resolveDueRange(preset: DuePreset): { dueFrom?: string; dueTo?: string } {
	if (preset === 'all') return {}
	const now = new Date()
	if (preset === 'today') {
		return { dueFrom: startOfDay(now).toISOString(), dueTo: endOfDay(now).toISOString() }
	}
	if (preset === 'week') {
		const end = new Date(now); end.setDate(end.getDate() + 7)
		return { dueFrom: startOfDay(now).toISOString(), dueTo: endOfDay(end).toISOString() }
	}
	if (preset === 'month') {
		const end = new Date(now); end.setMonth(end.getMonth() + 1)
		return { dueFrom: startOfDay(now).toISOString(), dueTo: endOfDay(end).toISOString() }
	}
	// overdue → past, only undone (status filter handles undone)
	return { dueTo: startOfDay(now).toISOString() }
}

function formatDue(iso: string): string {
	const d = new Date(iso)
	return `${d.toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: 'short' })}, ${d.toLocaleTimeString(DEFAULT_LOCALE, { hour: '2-digit', minute: '2-digit' })}`
}

function avatarInitials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean)
	if (parts.length === 0) return ''
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
	return (parts[0][0] + parts[1][0]).toUpperCase()
}

type SortField = 'kind' | 'dueDate'
type SortDir   = 'asc' | 'desc'

function TasksPage() {
	const { t } = useTranslation()
	const canCreate = useCan('create', 'Task')
	const { taskId: initialTaskId, due: initialDue } = Route.useSearch()
	const navigate = useNavigate()

	const [tab,         setTab]         = useState<TaskStatus>('all')
	const [kindFilter,  setKindFilter]  = useState<TaskKind | 'all'>('all')
	const [bindingFilter, setBindingFilter] = useState<TaskBindingKind | 'all'>('all')
	const [responsibleFilter, setResponsibleFilter] = useState<string>('all')
	const [duePreset,   setDuePreset]   = useState<DuePreset>(initialDue ?? 'all')
	const [search, setSearch] = useState('')
	const [page,   setPage]   = useState(1)

	const hasActiveFilters = kindFilter !== 'all' || bindingFilter !== 'all' || responsibleFilter !== 'all' || duePreset !== 'all' || !!search

	function resetFilters() {
		setKindFilter('all')
		setBindingFilter('all')
		setResponsibleFilter('all')
		setDuePreset('all')
		setSearch('')
		setPage(1)
	}
	const [sortField, setSortField] = useState<SortField>('dueDate')
	const [sortDir,   setSortDir]   = useState<SortDir>('asc')
	const [taskToDelete,   setTaskToDelete]   = useState<Task | null>(null)
	const [taskFormOpen,   setTaskFormOpen]   = useState(false)
	const [taskToEdit,     setTaskToEdit]     = useState<Task | undefined>(undefined)
	const [selectedTaskId, setSelectedTaskId] = useState<string | null>(initialTaskId ?? null)

	function handleCloseSheet() {
		setSelectedTaskId(null)
		void navigate({ to: '/tasks', search: {}, replace: true })
	}

	const dueRange = useMemo(() => resolveDueRange(duePreset), [duePreset])
	const sortKey: TaskSortKey = `${sortField}:${sortDir}` as TaskSortKey

	const { data, isLoading } = useListTasksQuery({
		status:        tab,
		kind:          kindFilter        === 'all' ? undefined : kindFilter,
		bindingKind:   bindingFilter     === 'all' ? undefined : bindingFilter,
		responsibleId: responsibleFilter === 'all' ? undefined : responsibleFilter,
		dueFrom:       dueRange.dueFrom,
		dueTo:         dueRange.dueTo,
		search:        search || undefined,
		sort:          sortKey,
		page,
		limit: PAGE_SIZE,
	})

	const { data: responsiblesData } = useListTaskResponsiblesQuery()
	// Встречи живут в своей таблице, а не среди задач, поэтому их приходится
	// подмешивать здесь: фильтр «Встреча» существовал и не находил ничего.
	const { data: meetingsData } = useListMeetingsQuery()
	const [updateMeeting] = useUpdateDealMeetingMutation()

	const [updateTask] = useUpdateTaskMutation()
	const [deleteTask, { isLoading: isDeleting }] = useDeleteTaskMutation()

	useGetTaskQuery(selectedTaskId!, { skip: !selectedTaskId })

	const tasks: Task[] = data?.items ?? []

	/**
	 * Встречи в облике задач.
	 *
	 * Строка помечена `meetingId` — по ней видно, куда адресовать действие:
	 * задача и встреча лежат в разных таблицах, и отправить `PATCH /tasks/:id`
	 * с идентификатором встречи значило бы получить 404 на каждый щелчок.
	 */
	const meetingRows: TaskRow[] = useMemo(() => {
		if (kindFilter !== 'all' && kindFilter !== 'MEETING') return []
		const q = search.trim().toLowerCase()
		return (meetingsData?.items ?? [])
			.filter((m) => {
				const done = m.status === 'COMPLETED' || m.status === 'CANCELLED'
				if (tab === 'open' && done) return false
				if (tab === 'done' && !done) return false
				if (dueRange.dueFrom && m.scheduledAt < dueRange.dueFrom) return false
				if (dueRange.dueTo && m.scheduledAt > dueRange.dueTo) return false
				if (q) {
					const haystack = `${m.address} ${m.notes ?? ''} ${m.dealLabel ?? ''}`.toLowerCase()
					if (!haystack.includes(q)) return false
				}
				return true
			})
			.map((m) => ({
				id: `meeting:${m.id}`,
				meetingId: m.id,
				dealId: m.dealId,
				kind: 'MEETING' as TaskKind,
				title: m.address,
				description: m.notes ?? '',
				binding: m.dealLabel
					? { kind: 'DEAL' as const, id: m.dealId, title: m.dealLabel }
					: undefined,
				dueDate: m.scheduledAt,
				isDone: m.status === 'COMPLETED',
				isOverdue: m.status === 'SCHEDULED' && m.scheduledAt < new Date().toISOString(),
				priority: 'NORMAL' as TaskPriority,
				createdAt: m.scheduledAt,
				updatedAt: m.scheduledAt,
			}))
	}, [meetingsData, kindFilter, tab, search, dueRange])

	// Встречи впереди: они привязаны ко времени, и пропустить их дороже.
	const rows: TaskRow[] = [...meetingRows, ...tasks]
	const total = (data?.total ?? 0) + meetingRows.length
	const responsibles = responsiblesData?.responsibles ?? []

	function toggleSort(field: SortField) {
		if (sortField === field) {
			setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortField(field)
			setSortDir('asc')
		}
	}

	function handleToggleDone(task: TaskRow, done: boolean) {
		if (task.meetingId) {
			// «Сделано» для встречи — это её статус, а не флаг: снятая галочка
			// возвращает её в запланированные, а не отменяет.
			void updateMeeting({
				id: task.meetingId,
				status: done ? 'COMPLETED' : 'SCHEDULED',
			})
			return
		}
		void updateTask({ id: task.id, body: { isDone: done } })
	}

	async function handleConfirmDelete() {
		if (!taskToDelete) return
		try {
			await deleteTask(taskToDelete.id).unwrap()
			setTaskToDelete(null)
		} catch (err) {
			console.error('deleteTask failed:', err)
		}
	}

	return (
		<div className="flex h-full flex-col">
			<PanelFilters
				filters={
					<>
						<Tabs value={tab} onValueChange={(v) => { setTab(v as TaskStatus); setPage(1) }}>
							<TabsList variant="pills">
								<TabsTrigger variant="pills" value="all">{t('tasks.tabs.all')}</TabsTrigger>
								<TabsTrigger variant="pills" value="open">{t('tasks.tabs.open')}</TabsTrigger>
								<TabsTrigger variant="pills" value="done">{t('tasks.tabs.done')}</TabsTrigger>
							</TabsList>
						</Tabs>
						<Select
							value={kindFilter}
							onValueChange={(v) => { setKindFilter(v as TaskKind | 'all'); setPage(1) }}
						>
							<SelectTrigger width="sm" tone="muted">
								<SelectValue placeholder={t('tasks.filters.kind')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('tasks.filters.allKinds')}</SelectItem>
								{TASK_KINDS.map((k) => (
									<SelectItem key={k} value={k}>{t(`tasks.kinds.${k}`)}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={bindingFilter}
							onValueChange={(v) => { setBindingFilter(v as TaskBindingKind | 'all'); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('tasks.filters.binding')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('tasks.filters.allBindings')}</SelectItem>
								{BINDING_KINDS.map((b) => (
									<SelectItem key={b} value={b}>{t(`tasks.bindingKinds.${b}`)}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={responsibleFilter}
							onValueChange={(v) => { setResponsibleFilter(v); setPage(1) }}
						>
							<SelectTrigger width="lg" tone="muted">
								<SelectValue placeholder={t('tasks.filters.responsible')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('tasks.filters.allResponsibles')}</SelectItem>
								{responsibles.map((r) => (
									<SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={duePreset}
							onValueChange={(v) => { setDuePreset(v as DuePreset); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('tasks.filters.due')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('tasks.filters.due')}</SelectItem>
								{DUE_PRESETS.map((d) => (
									<SelectItem key={d} value={d}>{t(`tasks.duePresets.${d}`, { defaultValue: d })}</SelectItem>
								))}
							</SelectContent>
						</Select>
					</>
				}
				actions={
					<>
						<SearchInput
							placeholder={t('tasks.search')}
							value={search}
							className="w-[200px]"
							onChange={(e) => { setSearch(e.target.value); setPage(1) }}
						/>
						{hasActiveFilters && (
							<Button size="sm" variant="ghost" onClick={resetFilters}>
								{t('tasks.filters.reset')}
							</Button>
						)}
						{canCreate && (
							<Button size="sm" onClick={() => { setTaskToEdit(undefined); setTaskFormOpen(true) }}>
								{t('tasks.create')}
							</Button>
						)}
					</>
				}
			/>

			{isLoading ? (
				<div className="flex flex-1 items-center justify-center">
					<Spinner />
				</div>
			) : tasks.length === 0 ? (
				<div className="flex flex-1 items-center justify-center">
					<p className="text-sm text-(--color-muted-foreground)">{t('tasks.empty')}</p>
				</div>
			) : (
				<>
					<div className="flex min-h-0 flex-1 flex-col overflow-auto px-4">
						<div className="overflow-hidden rounded-lg border border-(--color-border)">
							<Table className="table-fixed">
								<TableHeader>
									<TableRow className="hover:bg-transparent">
										<TableHead className="w-[180px]">
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												onClick={() => toggleSort('kind')}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('tasks.columns.kind')}
											</Button>
										</TableHead>
										<TableHead className="w-[24%] font-[500] text-(--color-muted-foreground)">
											{t('tasks.columns.title')}
										</TableHead>
										<TableHead className="w-[22%] font-[500] text-(--color-muted-foreground)">
											{t('tasks.columns.binding')}
										</TableHead>
										<TableHead className="w-[180px] font-[500] text-(--color-muted-foreground)">
											{t('tasks.columns.responsible')}
										</TableHead>
										<TableHead className="w-[140px]">
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												onClick={() => toggleSort('dueDate')}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('tasks.columns.due')}
											</Button>
										</TableHead>
										<TableHead className="w-12" />
									</TableRow>
								</TableHeader>
								<TableBody>
									{rows.map((task) => (
										<TableRow
											key={task.id}
											className="h-[72px] cursor-pointer"
											onClick={() => task.meetingId
												? void navigate({ to: '/deals/$dealId', params: { dealId: task.dealId! } })
												: setSelectedTaskId(task.id)}
										>
											<TableCell className="px-4" onClick={(e) => e.stopPropagation()}>
												<div className="flex items-center gap-3">
													<Checkbox
														checked={task.isDone ?? false}
														onCheckedChange={(checked) => handleToggleDone(task, checked === true)}
														aria-label={task.title || task.description}
													/>
													<span className="text-sm text-(--color-foreground)">
														{t(`tasks.kinds.${task.kind}`)}
													</span>
												</div>
											</TableCell>
											<TableCell className="px-4 text-sm text-(--color-foreground)">
												<div className="flex flex-col gap-0.5 min-w-0">
													<span className="line-clamp-2">{task.title || task.description}</span>
													{task.title && task.description && (
														<span className="line-clamp-1 text-(--color-muted-foreground)">{task.description}</span>
													)}
												</div>
											</TableCell>
											<TableCell className="px-4">
												{task.binding ? (
													<div className="flex flex-col gap-0.5 min-w-0">
														{task.binding.title && (
															<span className="text-sm font-[500] text-(--color-info) truncate cursor-pointer underline-offset-2 hover:underline">
																{task.binding.title}
															</span>
														)}
														{task.binding.subtitle && (
															<span className="text-sm text-(--color-muted-foreground) truncate">
																{task.binding.subtitle}
															</span>
														)}
													</div>
												) : (
													<span className="text-sm text-(--color-muted-foreground)">—</span>
												)}
											</TableCell>
											<TableCell className="px-4">
												{task.responsibleName ? (
													<div className="flex items-center gap-2">
														<Avatar className="size-6">
															{task.responsibleAvatar && <AvatarImage src={task.responsibleAvatar} alt={task.responsibleName} />}
															<AvatarFallback className="text-xs">{avatarInitials(task.responsibleName)}</AvatarFallback>
														</Avatar>
														<span className="text-sm text-(--color-foreground) truncate">{task.responsibleName}</span>
													</div>
												) : (
													<span className="text-sm text-(--color-muted-foreground)">—</span>
												)}
											</TableCell>
											<TableCell className="px-4">
												{task.dueDate ? (
													<span className={`text-sm ${task.isOverdue ? 'text-(--color-destructive)' : 'text-(--color-foreground)'}`}>
														{formatDue(task.dueDate)}
													</span>
												) : (
													<span className="text-sm text-(--color-muted-foreground)">—</span>
												)}
											</TableCell>
											<TableCell className="px-2" onClick={(e) => e.stopPropagation()}>
												<DropdownMenu>
													<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('tasks.actions.menu')} />}>
														<EllipsisVerticalIcon className="size-4" />
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end" className="w-auto min-w-40">
														{task.meetingId ? (
															// Встречу правят там, где она живёт — на вкладке
															// сделки: здесь нет ни адреса, ни времени в форме.
															<DropdownMenuItem onClick={() => void navigate({ to: '/deals/$dealId', params: { dealId: task.dealId! } })}>
																<PencilIcon />
																{t('tasks.actions.openMeeting')}
															</DropdownMenuItem>
														) : (
															<>
																<DropdownMenuItem onClick={() => { setTaskToEdit(task); setTaskFormOpen(true) }}>
																	<PencilIcon />
																	{t('tasks.actions.edit')}
																</DropdownMenuItem>
																<DropdownMenuSeparator />
																<DropdownMenuItem variant="destructive" onClick={() => setTaskToDelete(task)}>
																	<Trash2Icon />
																	{t('tasks.actions.delete')}
																</DropdownMenuItem>
															</>
														)}
													</DropdownMenuContent>
												</DropdownMenu>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</div>
					<PanelDown total={total} page={page} onPageChange={setPage} limit={PAGE_SIZE} />
				</>
			)}

		<TaskDetailSheet
			taskId={selectedTaskId}
			onClose={handleCloseSheet}
			onToggleDone={(task) => handleToggleDone(task, !(task.isDone ?? false))}
			onEdit={(task) => { setTaskToEdit(task); setTaskFormOpen(true); handleCloseSheet() }}
			onDelete={(task) => { setTaskToDelete(task); handleCloseSheet() }}
		/>

		<TaskFormDialog
				open={taskFormOpen}
				onOpenChange={(v) => { setTaskFormOpen(v); if (!v) setTaskToEdit(undefined) }}
				task={taskToEdit}
			/>

			<ConfirmDialog
				open={!!taskToDelete}
				onOpenChange={(v) => { if (!v) setTaskToDelete(null) }}
				title={t('tasks.actions.delete')}
				description={taskToDelete ? t('tasks.actions.deleteConfirm', { description: taskToDelete.description }) : undefined}
				confirmLabel={t('tasks.actions.delete')}
				cancelLabel={t('tasks.actions.cancel')}
				variant="destructive"
				loading={isDeleting}
				onConfirm={handleConfirmDelete}
			/>
		</div>
	)
}
